// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Import Tools
// Bulk CSV upload, import from Impact module, map-pick zones
// ═══════════════════════════════════════════════════════════════

import { useState } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Stack, SimpleGrid,
  Table, Textarea, Select, FileButton, Modal, Checkbox, Loader,
} from '../../components/gab/GabComponents';
import {
  IconUpload, IconFileSpreadsheet, IconMapPin, IconBuildingCommunity,
  IconCheck, IconX, IconAlertTriangle,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useDebrisStore } from '../../store/debrisStore';
import { useImpactStore } from '../../store/impact-store';
import { useAuthStore } from '../../store/authStore';
import type { DebrisZone, DebrisTruck, DebrisContractor } from '../../types/debris';
import type { Assessment } from '../../types/impact';
import { primary } from '../../lib/colors';

type ImportTarget = 'zones' | 'trucks' | 'contractors' | 'crews';

const IMPORT_TARGETS: { value: ImportTarget; label: string; columns: string }[] = [
  { value: 'zones',       label: 'Zones',          columns: 'zoneName, zoneCode, status, centerLat, centerLng, estimatedCubicYards, debrisTypes, notes' },
  { value: 'trucks',      label: 'Trucks',          columns: 'truckNumber, licensePlate, truckType, contractorId, lengthFt, widthFt, heightFt, cubicYardCapacity, status' },
  { value: 'contractors', label: 'Contractors',     columns: 'companyName, contractNumber, contactName, contactPhone, contactEmail, rateVegetative, rateCAndD, status' },
  { value: 'crews',       label: 'Crews / Drivers', columns: 'crewName, crewType, contractorId, driverName, driverPhone, driverEmail, assignedZoneId, status' },
];

const DAMAGE_COLORS: Record<string, string> = {
  destroyed:    'var(--color-error)',
  major:        '#F86A0B',
  minor:        '#C68700',
  affected:     'var(--color-primary-700)',
  inaccessible: '#939598',
};

// Estimate cubic yards of debris by damage category + structure type
function estimateCY(a: Assessment): string {
  const base: Record<string, number> = { destroyed: 75, major: 40, minor: 15, affected: 5, inaccessible: 25 };
  const multiplier = a.structureType?.toLowerCase().includes('commercial') ? 2 : 1;
  return String((base[a.damageCategory ?? ''] ?? 10) * multiplier);
}

export default function Import() {
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const { createZone, createTruck, createContractor, createCrew } = useDebrisStore();
  const { assessments, fetchAll } = useImpactStore();

  // ── CSV import state ────────────────────────────────────────────
  const [target,        setTarget]        = useState<ImportTarget>('zones');
  const [csvData,       setCsvData]       = useState('');
  const [parsedRows,    setParsedRows]    = useState<Record<string, string>[]>([]);
  const [headers,       setHeaders]       = useState<string[]>([]);
  const [importing,     setImporting]     = useState(false);
  const [importResults, setImportResults] = useState<{ success: number; failed: number } | null>(null);

  // ── Impact integration state ────────────────────────────────────
  const [showImpact,       setShowImpact]       = useState(false);
  const [loadingImpact,    setLoadingImpact]    = useState(false);
  const [importingImpact,  setImportingImpact]  = useState(false);
  const [selectedIds,      setSelectedIds]      = useState<Set<number>>(new Set());
  const [damageFilter,     setDamageFilter]     = useState<string | null>(null);
  const [impactResults,    setImpactResults]    = useState<{ success: number; failed: number } | null>(null);

  const openImpactModal = async () => {
    setShowImpact(true);
    setSelectedIds(new Set());
    setImpactResults(null);
    setLoadingImpact(true);
    await fetchAll();
    setLoadingImpact(false);
  };

  // Only show assessments for the active incident that have coordinates
  const incidentAssessments = assessments.filter(a =>
    String(a.incidentId) === incidentId && a.latitude && a.longitude
  );
  const filteredAssessments = damageFilter
    ? incidentAssessments.filter(a => a.damageCategory === damageFilter)
    : incidentAssessments;

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectedIds.size === filteredAssessments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAssessments.map(a => a.id)));
    }
  };

  const importFromImpact = async () => {
    const toImport = incidentAssessments.filter(a => selectedIds.has(a.id));
    if (!toImport.length) return;
    setImportingImpact(true);
    let success = 0; let failed = 0;
    let seq = 1;
    for (const a of toImport) {
      try {
        await createZone({
          zoneName:     a.address || `Impact Zone ${seq}`,
          zoneCode:     `IMP-${a.id}`,
          incidentId,
          centroidLat:  a.latitude?.toString() ?? '',
          centroidLng:  a.longitude?.toString() ?? '',
          status:       (a.damageCategory === 'destroyed' || a.damageCategory === 'major') ? 'active' : 'pending',
          estimatedCY:  estimateCY(a),
          notes:        [
            `Imported from Impact Assessment #${a.id}`,
            `Damage: ${a.damageCategory ?? 'unknown'}`,
            a.description ? `Description: ${a.description}` : '',
            a.estimatedLoss ? `Est. Loss: $${a.estimatedLoss.toLocaleString()}` : '',
          ].filter(Boolean).join('\n'),
        } as Partial<DebrisZone>);
        success++;
      } catch {
        failed++;
      }
      seq++;
    }
    setImpactResults({ success, failed });
    setImportingImpact(false);
    notifications.show({
      message: `${success} zone${success !== 1 ? 's' : ''} created from Impact assessments${failed ? `, ${failed} failed` : ''}`,
      color: failed > 0 ? '#C68700' : 'var(--color-success)',
    });
  };

  // ── CSV helpers ─────────────────────────────────────────────────
  const splitCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') { current += '"'; i++; }
        else if (ch === '"') { inQuotes = false; }
        else { current += ch; }
      } else {
        if (ch === '"') { inQuotes = true; }
        else if (ch === ',') { result.push(current.trim()); current = ''; }
        else { current += ch; }
      }
    }
    result.push(current.trim());
    return result;
  };

  const parseCSV = (text: string) => {
    const lines = text.trim().split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) {
      notifications.show({ message: 'CSV must have a header row and at least one data row', color: '#C68700' });
      return;
    }
    const hdrs = splitCSVLine(lines[0]);
    setHeaders(hdrs);
    const rows = lines.slice(1).map(line => {
      const vals = splitCSVLine(line);
      const row: Record<string, string> = {};
      hdrs.forEach((h, i) => { row[h] = vals[i] ?? ''; });
      return row;
    });
    setParsedRows(rows);
    setImportResults(null);
  };

  const handleFileUpload = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => { const text = e.target?.result as string; setCsvData(text); parseCSV(text); };
    reader.readAsText(file);
  };

  const handlePasteImport = () => {
    if (!csvData.trim()) { notifications.show({ message: 'Paste CSV data first', color: '#C68700' }); return; }
    parseCSV(csvData);
  };

  const runImport = async () => {
    if (parsedRows.length === 0) return;
    setImporting(true);
    let success = 0; let failed = 0;
    for (const row of parsedRows) {
      try {
        if (target === 'zones')       await createZone({ ...row, incidentId } as Partial<DebrisZone>);
        else if (target === 'trucks')      await createTruck({ ...row, incidentId } as Partial<DebrisTruck>);
        else if (target === 'contractors') await createContractor({ ...row, incidentId } as Partial<DebrisContractor>);
        else if (target === 'crews')       await createCrew({ ...row, incidentId } as unknown as Parameters<typeof createCrew>[0]);
        success++;
      } catch { failed++; }
    }
    setImportResults({ success, failed });
    setImporting(false);
    notifications.show({ message: `Import complete: ${success} succeeded, ${failed} failed`, color: failed > 0 ? '#C68700' : 'var(--color-success)' });
  };

  const downloadTemplate = () => {
    const tgt = IMPORT_TARGETS.find(t => t.value === target);
    if (!tgt) return;
    const blob = new Blob([tgt.columns + '\n'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `debris-${target}-template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const targetInfo = IMPORT_TARGETS.find(t => t.value === target);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3} c="black">Import Data</Title>
      </Group>

      {/* Import method cards */}
      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <Paper p="md" radius="md" withBorder>
          <Group gap="xs" mb="xs">
            <IconFileSpreadsheet size={20} color={primary[700]} />
            <Text fw={700}>CSV Upload</Text>
          </Group>
          <Text size="xs" c="dimmed" mb="sm">
            Upload a CSV file with zone, truck, contractor, or crew data
          </Text>
          <FileButton onChange={handleFileUpload} accept=".csv,text/csv">
            {(props) => <Button {...props} size="xs" variant="light" leftSection={<IconUpload size={14} />}>Choose File</Button>}
          </FileButton>
        </Paper>

        <Paper p="md" radius="md" withBorder>
          <Group gap="xs" mb="xs">
            <IconMapPin size={20} color="#16a34a" />
            <Text fw={700}>Paste CSV</Text>
          </Group>
          <Text size="xs" c="dimmed" mb="sm">
            Paste CSV data directly from a spreadsheet
          </Text>
          <Button size="xs" variant="light" onClick={handlePasteImport} leftSection={<IconCheck size={14} />}>
            Parse Pasted Data
          </Button>
        </Paper>

        <Paper p="md" radius="md" withBorder>
          <Group gap="xs" mb="xs">
            <IconBuildingCommunity size={20} color="#d97706" />
            <Text fw={700}>Impact Integration</Text>
          </Group>
          <Text size="xs" c="dimmed" mb="sm">
            Import damage assessment locations as debris zones
          </Text>
          {!activeIncident ? (
            <Text size="xs" c="dimmed">No active incident selected</Text>
          ) : (
            <Button size="xs" variant="light" color="#F86A0B" leftSection={<IconBuildingCommunity size={14} />}
              onClick={openImpactModal}>
              Import from Impact
            </Button>
          )}
        </Paper>
      </SimpleGrid>

      {/* Impact Integration Modal */}
      <Modal
        opened={showImpact}
        onClose={() => setShowImpact(false)}
        title="Import from Impact Assessments"
        size="xl"
      >
        {loadingImpact ? (
          <Group justify="center" py="xl"><Loader size="sm" /><Text size="sm" c="dimmed">Loading assessments...</Text></Group>
        ) : incidentAssessments.length === 0 ? (
          <Paper p="xl" ta="center">
            <IconAlertTriangle size={32} color="#939598" />
            <Text c="dimmed" mt="sm">No geolocated assessments found for this incident.</Text>
            <Text size="xs" c="dimmed" mt={4}>Only assessments with latitude and longitude can be imported as zones.</Text>
          </Paper>
        ) : (
          <Stack gap="md">
            <Group justify="space-between">
              <Group gap="sm">
                <Select
                  size="xs"
                  placeholder="All damage levels"
                  clearable
                  w={180}
                  value={damageFilter}
                  onChange={setDamageFilter}
                  data={[
                    { value: 'destroyed',    label: 'Destroyed' },
                    { value: 'major',        label: 'Major' },
                    { value: 'minor',        label: 'Minor' },
                    { value: 'affected',     label: 'Affected' },
                    { value: 'inaccessible', label: 'Inaccessible' },
                  ]}
                />
                <Text size="xs" c="dimmed">{filteredAssessments.length} assessment{filteredAssessments.length !== 1 ? 's' : ''}</Text>
              </Group>
              <Text size="xs" c="dimmed">{selectedIds.size} selected</Text>
            </Group>

            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={40}>
                      <Checkbox
                        size="xs"
                        checked={selectedIds.size === filteredAssessments.length && filteredAssessments.length > 0}
                        indeterminate={selectedIds.size > 0 && selectedIds.size < filteredAssessments.length}
                        onChange={toggleAll}
                      />
                    </Table.Th>
                    <Table.Th>Address</Table.Th>
                    <Table.Th w={120}>Damage</Table.Th>
                    <Table.Th w={80}>Est. CY</Table.Th>
                    <Table.Th w={100}>Coords</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredAssessments.map(a => (
                    <Table.Tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => toggleSelect(a.id)}>
                      <Table.Td onClick={e => e.stopPropagation()}>
                        <Checkbox size="xs" checked={selectedIds.has(a.id)} onChange={() => toggleSelect(a.id)} />
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" fw={500}>{a.address || '—'}</Text>
                        {a.description && <Text size="xs" c="dimmed" truncate style={{ maxWidth: 280 }}>{a.description}</Text>}
                      </Table.Td>
                      <Table.Td>
                        <Badge size="xs" color={DAMAGE_COLORS[a.damageCategory ?? ''] ?? '#939598'} tt="capitalize">
                          {a.damageCategory ?? 'unknown'}
                        </Badge>
                      </Table.Td>
                      <Table.Td><Text size="xs">{estimateCY(a)}</Text></Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed" ff="monospace">
                          {a.latitude?.toFixed(4)}, {a.longitude?.toFixed(4)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>

            {impactResults && (
              <Paper p="sm" radius="sm" withBorder
                style={{ borderColor: impactResults.failed > 0 ? '#C68700' : 'var(--color-success)' }}>
                <Group gap="xs">
                  {impactResults.failed === 0 ? <IconCheck size={16} color="var(--color-success)" /> : <IconX size={16} color="#F86A0B" />}
                  <Text size="sm">
                    <strong>{impactResults.success}</strong> zone{impactResults.success !== 1 ? 's' : ''} created
                    {impactResults.failed > 0 && <>, <strong>{impactResults.failed}</strong> failed</>}
                  </Text>
                </Group>
              </Paper>
            )}

            <Group justify="flex-end">
              <Button variant="subtle" size="xs" onClick={() => setShowImpact(false)}>Cancel</Button>
              <Button
                size="xs"
                color="#F86A0B"
                disabled={selectedIds.size === 0}
                loading={importingImpact}
                leftSection={<IconUpload size={14} />}
                onClick={importFromImpact}
              >
                Create {selectedIds.size > 0 ? selectedIds.size : ''} Zone{selectedIds.size !== 1 ? 's' : ''}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Target selector and template */}
      <Paper p="md" radius="md" withBorder>
        <Group gap="md" mb="md">
          <Select label="Import Target" data={IMPORT_TARGETS.map(t => ({ value: t.value, label: t.label }))}
            value={target} onChange={v => { setTarget(v as ImportTarget); setParsedRows([]); setHeaders([]); setImportResults(null); }}
            size="xs" w={180} />
          <div style={{ flex: 1 }}>
            <Text size="xs" c="dimmed">Expected columns:</Text>
            <Text size="xs" ff="monospace">{targetInfo?.columns}</Text>
          </div>
          <Button size="xs" variant="subtle" onClick={downloadTemplate}>Download Template</Button>
        </Group>

        <Textarea
          label="CSV Data"
          placeholder={`Paste CSV data here...\n${targetInfo?.columns}\nvalue1, value2, ...`}
          value={csvData}
          onChange={e => setCsvData(e.target.value)}
          autosize
          minRows={4}
          maxRows={10}
          mb="md"
        />

        {parsedRows.length > 0 && (
          <>
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={600}>Preview ({parsedRows.length} row{parsedRows.length !== 1 ? 's' : ''})</Text>
              <Button size="xs" onClick={runImport} loading={importing} leftSection={<IconUpload size={14} />}>
                Import {parsedRows.length} Records
              </Button>
            </Group>
            <div style={{ overflowX: 'auto', maxHeight: 400 }}>
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={40}>#</Table.Th>
                    {headers.map(h => <Table.Th key={h}>{h}</Table.Th>)}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {parsedRows.slice(0, 50).map((row, i) => (
                    <Table.Tr key={i}>
                      <Table.Td>{i + 1}</Table.Td>
                      {headers.map(h => <Table.Td key={h}><Text size="xs">{row[h] || '-'}</Text></Table.Td>)}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
            {parsedRows.length > 50 && <Text size="xs" c="dimmed" mt="xs">Showing first 50 of {parsedRows.length} rows</Text>}
          </>
        )}

        {importResults && (
          <Paper p="sm" mt="md" radius="sm" withBorder
            style={{ borderColor: importResults.failed > 0 ? '#C68700' : 'var(--color-success)' }}>
            <Group gap="xs">
              {importResults.failed === 0 ? <IconCheck size={16} color="var(--color-success)" /> : <IconX size={16} color="#F86A0B" />}
              <Text size="sm">
                <strong>{importResults.success}</strong> records imported successfully
                {importResults.failed > 0 && <>, <strong>{importResults.failed}</strong> failed</>}
              </Text>
            </Group>
          </Paper>
        )}
      </Paper>
    </Stack>
  );
}
